import { COMPONENT_HEIGHTS } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { useAddressedListFilters } from '@/hooks/objectives/useAddressedListFilters';
import type { Company } from '@/types/directory/Company';
import type { ReactNode } from 'react';

import {
    CompanyAttributeFilters,
    type FilterValueGroup,
} from '@/components/CompanyAttributeFilters/CompanyAttributeFilters';
import ObjectivePeriodFilter from '@/components/objectives/ObjectivePeriodFilter';
import ObjectiveSearchField from '@/components/objectives/ObjectiveSearchField';
import { defaultFiltersState } from '@/hooks/filters/useCompanyAttributesFilters';

interface ObjectiveListToolbarProps {
    /** The name searched and the attributes picked, as the address holds them. */
    filters: Pick<
        ReturnType<typeof useAddressedListFilters>,
        'searchText' | 'setSearchText' | 'attributeFilters' | 'setAttributeFilters'
    >;
    /** Whose attributes the filter menu offers. */
    company: Company | undefined;
    /**
     * Whether the filter menu (employee attributes, then the states) is offered: to a reader with a
     * roster of employees to narrow; someone who only reads their own objectives has none to narrow.
     */
    canNarrowRoster: boolean;
    /** The states the list is narrowed to, offered in the filter menu beside the attributes. */
    statusMenu: FilterValueGroup;
    /** Set beside the period lens, at the far end of the row: its help, for instance. */
    periodAddon?: ReactNode;
}

/**
 * The filters of an objectives list, the same on the objectives page and on a pillar's page: the
 * name searched, the employee attributes and objective states, and the period lens.
 */
export default function ObjectiveListToolbar({
    filters,
    company,
    canNarrowRoster,
    statusMenu,
    periodAddon,
}: ObjectiveListToolbarProps) {
    const { t } = useTranslation();

    return (
        <div
            className={'flex flex-wrap items-center gap-6'}
            data-testid={'objective-list-toolbar'}
        >
            <ObjectiveSearchField
                value={filters.searchText}
                onChange={filters.setSearchText}
                placeholder={t('objectives.searchEmployee')}
                dataTestId={'objectives-search'}
            />

            {canNarrowRoster && (
                <CompanyAttributeFilters
                    company={company}
                    selection={filters.attributeFilters ?? defaultFiltersState}
                    onFiltersChange={filters.setAttributeFilters}
                    valueGroup={statusMenu}
                    inline
                />
            )}

            {/* The cycle scopes everything below, so it stands apart at the far end of the row
                rather than among the filters. What is set beside it never wraps away from it: on a
                phone the picker gives up the room, not the addon, and the addon stays level with
                the picker (as tall as its trigger, the library's default select height) while
                custom dates unfold below it. */}
            <div
                className={'ml-auto flex items-center gap-3 max-sm:w-full max-sm:items-start'}
                data-testid={'objectives-period-row'}
            >
                <ObjectivePeriodFilter />

                {periodAddon && (
                    <div className={`flex ${COMPONENT_HEIGHTS.md} flex-shrink-0 items-center`}>
                        {periodAddon}
                    </div>
                )}
            </div>
        </div>
    );
}
